import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { config } from './lib/env-helpers.ts';
import PublicProviders from './container/context/public.providers.tsx';
import App from './App.tsx';
import i18n from '@shared/utils/i18n/index.ts';
import { GoogleReCaptchaProvider } from '@google-recaptcha/react';
import RecaptchaBadgeToggle from '@shared/layouts/public-layout/recaptcha-badge-toggle.tsx';
import * as Sentry from '@sentry/react';
import { initSentry } from './sentry.ts';
import { Button, Result } from 'antd';
import { RiRestartLine } from 'react-icons/ri';

// window type
interface Navigation extends EventTarget {
  addEventListener(
    type: 'navigate',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    listener: (this: Navigation, ev: Event) => any,
    options?: boolean | AddEventListenerOptions
  ): void;
}
declare global {
  interface Window {
    navigation?: Navigation; // if supported
  }
}

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);

dayjs.updateLocale('en', {
  relativeTime: {
    ...dayjs.Ls.en.relativeTime,
    mm: '%d min',
    s: 'just now', // convert `a few seconds ago` to `just now` when using `fromNow fn`
    past: (s: string) => (s === 'just now' ? s : `${s} ago`),
  },
});

initSentry();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={
        <Result
          status="404"
          title="Client Error"
          subTitle="Sorry, Something went wrong. Please try again later."
          extra={
            <Button variant="outlined" type="default" icon={<RiRestartLine />} onClick={() => window.location.reload()}>
              {i18n.t('reload')}
            </Button>
          }
        />
      }
    >
      <GoogleReCaptchaProvider
        type="v3"
        siteKey={config.RECAPTCHA_KEY}
        scriptProps={{
          async: true,
        }}
      >
        <BrowserRouter>
          <RecaptchaBadgeToggle />
          <PublicProviders>
            <App />
          </PublicProviders>
        </BrowserRouter>
      </GoogleReCaptchaProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
);
